// Sprint CRM-6 — tab «Lead» della scheda veicolo: Elenco (VistaRigheCrm) e Statistiche (stesso calcolo della dashboard)
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import VistaRigheCrm from "./VistaRigheCrm";
import { Box, Riquadro, TabellaStati } from "./CrmDashboard";
import { Ferma, Libera, PrezzoVendita } from "./CrmOpportunita";
import { STATI_OPP, errore } from "./crmComuni";

const TUTTI_STATI = ["aperta", "vinta", "persa", "abbandonata"];
const PERIODI = { tutto: "Tutto il periodo", mese: "Mese corrente", 30: "Ultimi 30 giorni", libero: "Dal… al…" };
const PARTENZA = { order: ["descrizione", "pipeline", "stato", "motivo", "contatto", "fase", "titolare", "fonte", "created_at", "updated_at"],
  visible: ["descrizione", "pipeline", "stato", "motivo", "contatto", "fase", "titolare", "fonte", "created_at"] };
const VALORE = { descrizione: (o) => (o.descrizione || "").toLowerCase(), contatto: (o) => (o.contatto?.nome || "").toLowerCase(),
  fase: (o) => o.fase_posizione ?? 1e6, stato: (o) => TUTTI_STATI.indexOf(o.stato), prezzo_vendita: (o) => o.prezzo_vendita_mostrato,
  prezzo_web: (o) => o.prezzo_web_mostrato, titolare: (o) => (o.titolare_nome || "").toLowerCase(), fonte: (o) => (o.fonte_nome || "").toLowerCase(),
  created_at: (o) => o.created_at || "", updated_at: (o) => o.updated_at || "", giorni: (o) => o.giorni_in_fase };

export function ordinaLead(items, sort) {
  const v = VALORE[sort.by] || VALORE.created_at;
  const pieni = items.filter((o) => v(o) != null && v(o) !== "");
  const vuoti = items.filter((o) => v(o) == null || v(o) === "");
  pieni.sort((a, b) => (v(a) < v(b) ? -1 : v(a) > v(b) ? 1 : 0) * (sort.order === "desc" ? -1 : 1));
  return [...pieni, ...vuoti];
}

function Elenco({ items }) {
  const [sort, setSort] = useState({ by: "created_at", order: "desc" });
  const righe = useMemo(() => ordinaLead(items, sort), [items, sort]);
  return <VistaRigheCrm items={righe} sort={sort} setSort={setSort} pid="veicolo" partenza={PARTENZA} vuoto="Nessuna lead"
    celle={{ Ferma, Libera, PrezzoVendita }} />;
}

const nessunLink = () => ({ pid: null, to: null });

// Lead del veicolo visibili all'utente (senza accesso al CRM o in errore: nessuna, la scheda non compare)
export function useLeadVeicolo(vehicleId, giro) {
  const [lead, setLead] = useState([]);
  useEffect(() => {
    if (!vehicleId) { setLead([]); return; }
    api.get(`/crm/veicoli/${vehicleId}/opportunita`).then((r) => setLead(r.data || [])).catch(() => setLead([]));
  }, [vehicleId, giro]);
  return lead;
}

function Selettori({ d, s, set }) {
  return (
    <div className="flex items-end gap-3 flex-wrap" data-testid="lead-stat-selettori">
      <div><Label className="text-xs text-slate-500">Pipeline</Label>
        <Select value={s.pipeline} onValueChange={(v) => set({ pipeline: v })}>
          <SelectTrigger className="w-56 max-w-full" data-testid="lead-stat-pipeline"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="_tutte">Tutte</SelectItem>{d.pipelines.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
        </Select></div>
      <div><Label className="text-xs text-slate-500">Periodo</Label>
        <Select value={s.periodo} onValueChange={(v) => set({ periodo: v })}>
          <SelectTrigger className="w-48" data-testid="lead-stat-periodo"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(PERIODI).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select></div>
      {s.periodo === "libero" && <>
        <Input type="date" className="w-40" value={s.dal} onChange={(e) => set({ dal: e.target.value })} data-testid="lead-stat-dal" />
        <Input type="date" className="w-40" value={s.al} onChange={(e) => set({ al: e.target.value })} data-testid="lead-stat-al" />
      </>}
      {d.periodo.dal && <span className="text-xs text-slate-500 pb-2" data-testid="lead-stat-periodo-testo">
        dal {d.periodo.dal.split("-").reverse().join("/")} al {d.periodo.al.split("-").reverse().join("/")}</span>}
    </div>
  );
}

export function ContenutoStatistiche({ d, s }) {
  const n = { cid: null, pid: null };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Riquadro testid="lead-stat-totale" titolo="Lead inserite nel periodo"
          sotto={<span className="space-x-1">{TUTTI_STATI.map((x, i) => <span key={x} data-testid={`lead-stat-${x}`}>{i ? "· " : ""}{d.inserite[x]} {STATI_OPP[x].toLowerCase()}</span>)}</span>}>
          <span data-testid="lead-stat-totale-n">{d.inserite.inserite}</span></Riquadro>
        <Riquadro testid="lead-stat-in-corso" titolo="Lead in corso oggi"><span data-testid="lead-stat-in-corso-n">{d.in_corso}</span></Riquadro>
      </div>
      <Box testid="lead-stat-per-fonte" titolo="Per fonte" nota="Lead inserite nel periodo">
        <TabellaStati righe={d.per_fonte} prefisso="lead-stat-fonte" link={nessunLink} n={n} intestazione="Fonte" /></Box>
      <Box testid="lead-stat-motivi" titolo="Motivi di perdita" nota="Lead perse nel periodo">
        {d.motivi.length === 0 ? <div className="text-sm text-slate-500" data-testid="lead-stat-motivi-vuoto">—</div> : (
          <table className="w-full text-sm"><tbody>{d.motivi.map((m) => (
            <tr key={m.id || "_senza"} className="border-t first:border-t-0 border-slate-100 tabular-nums" data-testid={`lead-stat-motivo-${m.id || "senza"}`}>
              <td className="py-2">{m.nome}</td><td className="text-right">{m.n}</td></tr>))}</tbody></table>)}
      </Box>
      {d.per_utente ? <Box testid="lead-stat-per-utente" titolo="Per utente" nota="Lead inserite nel periodo, stato di oggi">
        <TabellaStati righe={d.per_utente} prefisso="lead-stat-utente" link={nessunLink} n={n} intestazione="Titolare" conInCorso /></Box>
        : s.pipeline === "_tutte" && d.utente_in_una_pipeline && <div className="text-sm text-slate-500" data-testid="lead-stat-utente-invito">
          Per vedere i numeri per utente scegli una pipeline in cui vedi tutte le lead</div>}
    </div>
  );
}

function Statistiche({ vehicleId, companyId }) {
  const [s, setS] = useState({ pipeline: "_tutte", periodo: "tutto", dal: "", al: "" });
  const [d, setD] = useState(null);
  const set = (x) => setS((v) => ({ ...v, ...x }));
  useEffect(() => {
    if (s.periodo === "libero" && !(s.dal && s.al)) return;
    const p = new URLSearchParams({ company_id: companyId, vehicle_id: vehicleId, periodo: s.periodo, utente: "tutti" });
    if (s.pipeline !== "_tutte") p.set("pipeline_id", s.pipeline);
    if (s.periodo === "libero") { p.set("dal", s.dal); p.set("al", s.al); }
    api.get(`/crm/dashboard?${p}`).then((r) => setD(r.data)).catch((e) => toast.error(errore(e)));
  }, [vehicleId, companyId, s]);
  if (!d) return null;
  return <div className="space-y-4"><Selettori d={d} s={s} set={set} /><ContenutoStatistiche d={d} s={s} /></div>;
}

export default function LeadVeicolo({ items, vehicleId, companyId }) {
  const [sotto, setSotto] = useState("elenco");
  const Bott = ({ k, children }) => (
    <button type="button" onClick={() => setSotto(k)} data-testid={`lead-sottoscheda-${k}`}
      className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${sotto === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
      {children}</button>);
  return (
    <div className="space-y-4" data-testid="veicolo-lead">
      <div className="flex gap-2"><Bott k="elenco">Elenco</Bott><Bott k="statistiche">Statistiche</Bott></div>
      {sotto === "elenco" ? <Elenco items={items} /> : <Statistiche vehicleId={vehicleId} companyId={companyId} />}
    </div>
  );
}
